import "./styles/survey.css";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Modal, Button, Loader } from "semantic-ui-react";

import { CSSTransition, TransitionGroup } from "react-transition-group";
import SurveyLoader from "./components/SurveyLoader";

// Constants
import {
    SG_START_BRIEF_SURVEY,
    NEW_VALIDATION_TOKEN,
    SG_UPDATE_BRIEF_RESPONSE
} from "constants/actions";

import {
    translate,
    SURVEY_STATUS_COMPLETED,
    SURVEY_STATUS_CLOSED,
    SURVEY_STATUS_CANCELLED,
    SURVEY_STATUS_DELETED,
    SURVEY_STATUS_UNKNOWN,
    MISSING_OR_INVALID_TOKEN,
    THANKYOU_MESSAGE,
    DEFAULT_STATUS_TITLE,
    ASSESSMENT_PERIOD_NOT_OPEN
} from "./constants/messaging";

// Handlers
import { arrangeQuestions } from "./handlers/arrangeQuestions";
import { getNextQuestion } from "./handlers/getNextQuestion";
import { buildSequence } from "./handlers/buildSequence";
import { submitCompletedSurvey as submitHelper } from "./handlers/submitCompletedSurvey";
import { buildFeedbackMinMaxQuestions } from "./handlers/buildFeedbackMinMaxQuestions";
import { buildModularSurvey } from "./handlers/buildModularSurvey";

// Question types
import {
    introductionTypeQuestion,
    categoryTypeQuestion,
    questionTypeQuestion,
    feedbackTypeQuestion,
    modularOutcomeTypeQuestion,
    followupAiTypeQuestion,
    completedTypeQuestion,
    submitTypeQuestion,
} from "./constants/questionTypes";

// Question components
import SurveyQuestion from "./question-types/SurveyQuestion";
import ResponseProgressBar from "./components/ResponseProgressBar";
import FeedbackQuestion from "./question-types/FeedbackQuestion";
import CategoryQuestion from "./question-types/CategoryQuestion";
import SurveyIntro from "./intro-screens/SurveyIntro";
import SurveyNavigationButtons from "./components/SurveyNavigationButtons";
import FollowUp from "./question-types/AIFollowUpQuestion";
import SurveyCompleted from "./outro-screens/SurveyCompleted";
import SurveyStatus from "./outro-screens/SurveyStatus";
import OutcomeQuestion from "./question-types/OutcomeQuestion";

// Other components
import { Back, ButtonContainer, SurveyContainer, ModuleContainer } from "./styles/styled-components";
import { SurveyInProgressDialog } from "./components/SurveyInProgressDialog";
import { NOT_ALL_QUESTIONS_ANSWERED, NOT_ALL_QUESTIONS_ANSWERED_MESSAGE } from "./constants/constants";
import { ERROR_ON_SUBMISSION } from "./constants/messaging";

let LOCAL_VALIDATION_TOKEN = localStorage.getItem("validation_token");

const SurveyMain = () => {
    const dispatch = useDispatch();

    let { token } = useParams();

    const [noResponse, setNoResponse] = useState(false);
    const [ruleFeedback, setRuleFeedback] = useState([]);
    const [max_followup, setMaxFollowup] = useState(0);
    const [min_followup, setMinFollowup] = useState(0);

    const sequenceBuiltRef = useRef(false);

    const [questionSequenceMap, setQuestionSequenceMap] = useState(false);
    const [totalQuestions, setTotalQuestions] = useState({
        total: 0,
        runningTotal: 0,
        questions: 0,
        culture: 0,
        sequence: {},
    });

    const [showingStoredResponse, setShowingStoredResponse] = useState(false);
    const [categoryQues, setCategoryQues] = useState([]);
    const [employeeCategory, setEmployeeCategory] = useState([]);

    const [briefResponseId, setBriefResponseId] = useState(false);
    const [storedResponseFound, setStoredResponseFound] = useState(false);

    const [briefResponse, setBriefResponse] = useState([]);
    const [storedResponse, setStoredResponse] = useState(false);
    const [questionNo, setQuestionNo] = useState(0);
    const [visualQuestionNo, setVisualQuestionNo] = useState(0);

    const [questionAnswers, setQuestionAnswers] = useState({
        categories: {},
        questions: {},
        comments: {},
    });

    const [formPayload, setFormPayload] = useState(false);

    const [surveyStatus, setSurveyStatus] = useState(0);
    // TP: deprecated surveyDeleted state
    //    const [surveyDeleted, setSurveyDeleted] = useState(false);

    // TP: deleted on should be changed to completed on.
    const [surveyDeletedOn, setSurveyDeletedOn] = useState(false);

    const [neverAskResponse, setNeverAskResponse] = useState(false);
    const [prevFoundResponse, setPrevFoundResponse] = useState(false);

    const [organizationName, setOrganizationName] = useState("");

    const [language, setLanguage] = useState("EN");

    const [submitError, setSubmitError] = useState(false);
    const [organizationStyles, setOrganizationStyles] = useState({});

    const [surveyFeedbackResp, setSurveyFeedbackResp] = useState(false);
    const [surveyModularResp, setSurveyModularResp] = useState(false);
    const [modSurveyRule, setModSurveyRule] = useState([]);

    const [currentQuestionDet, setCurrentQuestionDet] = useState({});
    const [currentQuestionNo, setCurrentQuestionNo] = useState(1);
    const [direction, setDirection] = useState("slide");
    const [isVisible, setIsVisible] = useState(true);
    const [surveyFinished, setSurveyFinished] = useState(false);

    const [tokenError, setTokenError] = useState(false);

    const TRANSITION_MS = 900;

    const { get_brief_response, get_errors } = useSelector(
        (state) => ({
            get_brief_response: state.briefresponse,
            get_errors: state.errors,
        }),
        shallowEqual
    );

    const questionList = useMemo(
        () => arrangeQuestions(get_brief_response?.questions) || [],
        [get_brief_response?.questions]
    );

    useEffect(() => {
        const setVh = () => {
            let vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty("--vh", `${vh}px`);
        };

        // Listen to the resize event
        window.addEventListener("resize", setVh);

        // Set the initial value
        setVh();

        // Remove event listener on cleanup
        return () => {
            window.removeEventListener("resize", setVh);
        };
    }, []);

    useEffect(() => {
        const onTouchMove = (e) => {
            // Allow scrolling within scrollable elements (like dropdowns)
            const target = e.target;
            const scrollableParent = target.closest('[role="listbox"], .scrollable, [data-scrollable]');
            if (scrollableParent) {
                return; // Allow scroll
            }
            e.preventDefault(); // Prevent scroll everywhere else
        };
        document.body.addEventListener("touchmove", onTouchMove, { passive: false });
        return () => {
            document.body.removeEventListener("touchmove", onTouchMove);
        };
    }, []);

    useEffect(() => {
        const br = get_brief_response;
        if (!br) return;

        // Core brief mirror
        setBriefResponse(br);
        // Mark initial load complete once we have the brief
        setInitialLoad(false);

        // Simple flags / numbers
        setMinFollowup(br?.min_followup || 0);
        setMaxFollowup(br?.max_followup || 0);

        // Determine whether the survey is completed (show completed UI immediately)
        const isCompleted = Number(br?.survey_status) === 4;

        // TP: deprecated surveyDeleted state
        //        setSurveyDeleted(!!isCompleted);
        setSurveyDeletedOn(br?.completed_on || br?.deleted_on || false);
        setSurveyStatus(br?.survey_status || 0);
        setBriefResponseId(br?.id);

        // Styles & employee
        setOrganizationStyles(br?.organization?.styles || {});
        setEmployeeCategory(br?.employee?.category || []);

        // Organization name (handles “styles.organization == 1” vs org + primary category)
        const org = br?.organization;
        const orgName = br?.organization?.name;

        setOrganizationName(orgName || "your organization");

        // Inject stored response (answers / modular / feedback) only for non-demo surveys
        const stored = br?.stored_response || {};
        if (!br?.is_demo) {
            if (stored.answers) setQuestionAnswers(stored.answers);
            if (stored.modular_response) setSurveyModularResp(stored.modular_response);
            if (Array.isArray(stored.feedback)) setSurveyFeedbackResp(stored.feedback);
        }

        // Modular survey
        const surveyComponents = org?.styles?.survey_sequence || [];
        setModSurveyRule(buildModularSurvey({ surveyComponents }));

        // Stored-response token match (show “resume previous answers” prompt)
        const tokenA = (br?.validation_token || "").toLowerCase().trim();           // server
        const tokenB = (LOCAL_VALIDATION_TOKEN || "").toLowerCase().trim();         // local
        const sToken = (stored?.validation_token || "").toLowerCase().trim();       // saved snapshot

        // Consider it "has progress" if we have any of the usual resume hints
        const hasProgress =
            !!stored?.questionSequenceMap ||
            !!stored?.current_question ||
            (stored?.answers && Object.keys(stored.answers).length > 0) ||
            (Array.isArray(stored?.feedback) && stored.feedback.length > 0) ||
            (stored?.modular_response && Object.keys(stored.modular_response).length > 0);

        // Relaxed rules:
        // - require a server token (tokenA) and some progress
        // - accept either a matching local token OR no local token yet
        // - do NOT require sToken (older snapshots may not carry it)
        const tokensOkay =
            !!tokenA && (tokenA === tokenB || !tokenB);

        // If there's no local token but server provided one, adopt it so future saves align
        if (!tokenB && tokenA) {
            localStorage.setItem("validation_token", tokenA);
            LOCAL_VALIDATION_TOKEN = tokenA;
        }

        if (hasProgress && tokensOkay && !br?.is_demo) {
            setStoredResponseFound(true);
            setStoredResponse(stored);
        } else {
            setStoredResponseFound(false);
            setStoredResponse(false);
        }


    }, [get_brief_response, showingStoredResponse]);

    useEffect(() => {
        const seq = questionSequenceMap || totalQuestions.sequence;
        if (!seq) {
            setRuleFeedback([]);
            return;
        }

        const RuleRun = buildFeedbackMinMaxQuestions(
            get_brief_response?.questions, // the big questions payload with .factors[]
            min_followup,
            max_followup,
            seq
        );

        setRuleFeedback(Array.isArray(RuleRun) ? RuleRun : []);
    }, [
        min_followup,
        max_followup,
        questionSequenceMap,
        totalQuestions.sequence,
        get_brief_response?.questions,
        currentQuestionNo,
        showingStoredResponse,
    ]);


    useEffect(() => {
        if (!showingStoredResponse || !storedResponse) return;

        // 1) Restore answers/state
        const {
            answers,
            feedback,
            modular_response,
            questionSequenceMap: storedSeq, // avoid shadowing state var
        } = storedResponse;

        if (answers) setQuestionAnswers(answers);
        if (Array.isArray(feedback)) setSurveyFeedbackResp(feedback);
        if (modular_response) setSurveyModularResp(modular_response);

        // 2) Ensure intro questions are marked as completed so we skip them
        const updatedSeq = { ...storedSeq };
        const ids = Object.keys(updatedSeq).map(Number).sort((a, b) => a - b);
        if (!ids.length) return;

        // Mark all intro questions as completed
        ids.forEach(id => {
            if (updatedSeq[id]?.quesType === introductionTypeQuestion) {
                updatedSeq[id] = {
                    ...updatedSeq[id],
                    clicked: true,
                    response: true
                };
            }
        });

        setQuestionSequenceMap(updatedSeq);

        // 3) Compute where to resume (first unanswered, non-skipped, answerable step)
        // Steps that actually collect answers
        const ANSWER_TYPES = new Set([
            categoryTypeQuestion,
            questionTypeQuestion,
            feedbackTypeQuestion,
            followupAiTypeQuestion,
            modularOutcomeTypeQuestion,
        ]);

        const isAnswerStep = (step) => ANSWER_TYPES.has(step?.quesType);

        // Count as answered only if it has a real response (not boolean true from intro/submit)
        const isAnswered = (step) => {
            if (!isAnswerStep(step)) return false;
            const r = step?.response;
            if (r === true || r == null || r === false) return false;
            return true;
        };

        // First unanswered answerable step (preferred)
        const firstUnanswered = ids.find(
            (n) => !updatedSeq[n]?.skip && isAnswerStep(updatedSeq[n]) && !isAnswered(updatedSeq[n])
        );

        // Fallbacks: submit page if all answered, else last id
        let resumeAt = firstUnanswered;
        if (resumeAt == null) {
            const submitId = ids.find((n) => updatedSeq[n]?.quesType === submitTypeQuestion);
            resumeAt = submitId ?? ids[ids.length - 1];
        }

        setCurrentQuestionNo(resumeAt);
        setQuestionNo(resumeAt);
        setCurrentQuestionDet(updatedSeq[resumeAt]);
        setVisualQuestionNo(updatedSeq[resumeAt]?.visualTotal ?? 0);

        // 4) Seed formPayload so subsequent saves include the restored state
        const seededPayload = {
            ...(storedResponse || {}),
            questionSequenceMap: updatedSeq,
            validation_token: LOCAL_VALIDATION_TOKEN,
            intro_completed: true, // Mark intro as completed
        };
        setFormPayload((prev) => ({ ...(prev || {}), ...seededPayload }));

        // Persist the restored snapshot immediately so server matches client
        if (briefResponseId) {
            dispatch({
                type: SG_UPDATE_BRIEF_RESPONSE,
                payload: {
                    id: briefResponseId,
                    query: `?token=${token}`,
                    validation_token: LOCAL_VALIDATION_TOKEN,
                    stored_response: seededPayload,
                },
            });
        }
    }, [showingStoredResponse, storedResponse, totalQuestions]);


    useEffect(() => {
        setEmployeeCategory(get_brief_response?.employee?.category || []);
        setOrganizationStyles(get_brief_response?.organization?.styles || {});
        setCategoryQues(
            (get_brief_response?.categories || []).sort(
                (a, b) => (a?.priority_number ?? 0) - (b?.priority_number ?? 0)
            )
        );
    }, [
        get_brief_response?.employee?.category,
        get_brief_response?.categories,
        get_brief_response?.organization?.styles,
    ]);

    // the core list for managing survey within the app
    useEffect(() => {
        if (sequenceBuiltRef.current) return;
        if (!categoryQues?.length || !questionList?.length) return;

        const { sequence, meta, prevFoundResponse, neverAskResponse } = buildSequence({
            categoryQues,
            employeeCategory,
            questionList,
            organizationName,
            outcomes: get_brief_response?.outcomes,
            min_followup,
            max_followup,
            modSurveyRule,
        });

        setPrevFoundResponse(prevFoundResponse);
        setNeverAskResponse(neverAskResponse);
        setTotalQuestions({ ...meta, sequence });

        sequenceBuiltRef.current = true;
    }, [
        categoryQues?.length,
        questionList?.length,
        get_brief_response?.outcomes,
        min_followup,
        max_followup,
        modSurveyRule,
        organizationName,
    ]);

    useEffect(() => {
        const seq = questionSequenceMap || totalQuestions.sequence;
        if (!seq) return;
        setCurrentQuestionDet(seq[currentQuestionNo] || seq[1]);
    }, [questionSequenceMap, totalQuestions, currentQuestionNo]);


    const formPayloadRef = useRef(formPayload);
    useEffect(() => { formPayloadRef.current = formPayload; }, [formPayload]);

    function handleNext() {
        setDirection("slide");

        const seq = questionSequenceMap || totalQuestions.sequence;
        if (!seq) return;

        const base = formPayloadRef.current || {};
        const newPayload = {
            ...base,
            current_question: currentQuestionNo,
            current_answer: currentQuestionNo,
            high_answer: Math.max(base.high_answer || 0, currentQuestionNo),
            visual_question: seq[currentQuestionNo]?.visualTotal ?? 0,
            questionSequenceMap: seq,
            validation_token: LOCAL_VALIDATION_TOKEN,
        };

        if (briefResponseId) {
            dispatch({
                type: SG_UPDATE_BRIEF_RESPONSE,
                payload: {
                    id: briefResponseId,
                    query: `?token=${token}`,
                    validation_token: LOCAL_VALIDATION_TOKEN,
                    stored_response: newPayload,
                },
            });
        }

        const nextId = getNextQuestion(
            seq,
            currentQuestionNo,
            totalQuestions?.runningTotal || currentQuestionNo
        );

        // delay to let CSS direction settle before swapping questions
        setTimeout(() => {
            setQuestionNo(nextId);
            setVisualQuestionNo(seq[nextId]?.visualTotal);
            setCurrentQuestionNo(nextId);
        }, 10);
    }


    function prevQuestion() {
        let _questionNo = currentQuestionNo - 1;
        while (_questionNo >= 1) {
            if (questionSequenceMap?.[_questionNo]?.skip) {
                _questionNo--;
            } else {
                const _checkFdbk = questionSequenceMap[_questionNo]?.response?.skip;
                const _checkResp = questionSequenceMap[_questionNo]?.response;
                // check if follow up and feedback is skipped
                if (_checkFdbk) {
                    _questionNo--;
                } else if (!_checkResp) {
                    return _questionNo;
                } else {
                    setQuestionNo(_questionNo);
                    setVisualQuestionNo(currentQuestionDet?.visualTotal);
                    return _questionNo;
                }
            }
        }
        return currentQuestionNo;
    }

    const handleBack = () => {
        if (currentQuestionNo > 0) {
            setDirection("slide-back");
            // persist with api
            const timer1 = setTimeout(() => {
                if (briefResponseId) {
                    dispatch({
                        type: SG_UPDATE_BRIEF_RESPONSE,
                        payload: {
                            id: briefResponseId,
                            query: `?token=${token}`,
                            validation_token: LOCAL_VALIDATION_TOKEN,
                            ...(formPayload && {
                                stored_response: {
                                    ...formPayload,
                                    validation_token: LOCAL_VALIDATION_TOKEN,
                                },
                            }),
                        },
                    });
                }
                setCurrentQuestionNo(prevQuestion());
            }, 10);
            return () => clearTimeout(timer1);
        }
    };

    function resetValidationToken(newValidationToken) {
        LOCAL_VALIDATION_TOKEN = newValidationToken;
    }

    useEffect(() => {
        if (get_brief_response?.id) {
            setNoResponse(false);
        } else {
            setNoResponse(true);
        }
    }, [get_brief_response]);




    // Retrieve the survey brief and begin the survey
    useEffect(() => {
        if (token) {
            // Flag this as the initial fetch/load
            setInitialLoad(true);
            dispatch({
                type: SG_START_BRIEF_SURVEY,
                payload: `?token=${token}&_=${Date.now()}`, // cache-bust with date
            });
        }
    }, [dispatch, token]);


    // Submit the final survey response
    function submitCompletedSurvey() {
        setSurveyFinished(true);

        if (get_brief_response?.is_demo)
            return;

        const result = submitHelper({
            dispatch,
            briefResponse,
            totalQuestions,
            questionAnswers,
            prevFoundResponse,
            neverAskResponse,
            surveyFeedbackResp,
            surveyModularResp,
            questionSequenceMap,
        });

        if (result === NOT_ALL_QUESTIONS_ANSWERED) {
            setSubmitError(translate(NOT_ALL_QUESTIONS_ANSWERED_MESSAGE, language));
            setSurveyFinished(false);
            return;
        }
    }


    function StartSurvey() {
        setDirection("slide");

        const seq = { ...(totalQuestions?.sequence || {}) };
        if (seq[1]) {
            seq[1] = { ...seq[1], clicked: true, response: seq[1].response || true };
        }
        setQuestionSequenceMap(seq);

        const max = totalQuestions?.runningTotal;
        if (!seq || !max || Object.keys(seq).length < 5) {
            console.warn("Sequence not ready yet");
            return;
        }

        if (!seq[1].clicked) seq[1].clicked = true;
        if (!seq[1].response) seq[1].response = true;

        let NewValidationToken = LOCAL_VALIDATION_TOKEN || uuidv4();
        if (!LOCAL_VALIDATION_TOKEN) {
            localStorage.setItem("validation_token", NewValidationToken);
        }

        const nextId = getNextQuestion(seq, 1, max);

        setFormPayload({
            current_question: 1,
            current_answer: 0,
            high_answer: 0,
            answers: { categories: {}, questions: {}, comments: {} },
            complete: false,
            intro_completed: true,
            modular_response: {},
            feedback: [],
            questionSequenceMap: seq,
            validation_token: NewValidationToken,
        });

        dispatch({ type: NEW_VALIDATION_TOKEN, payload: NewValidationToken });

        setQuestionSequenceMap(seq);

        // delay to allow the "slide" direction to apply
        setTimeout(() => {
            setCurrentQuestionNo(nextId);
            setQuestionNo(nextId);
            setVisualQuestionNo(seq[nextId]?.visualTotal);
        }, 10);
    }

    // updated answers
    function saveUpdatedAnswers(curQuesNo, response, typeOf) {
        const curStep = questionSequenceMap[curQuesNo];

        const nextAnswers = {
            categories: { ...questionAnswers.categories },
            questions: { ...questionAnswers.questions },
            comments: { ...questionAnswers.comments },
        };
        let nextFeedback = Array.isArray(surveyFeedbackResp) ? [...surveyFeedbackResp] : [];

        const nextModular = { ...(surveyModularResp || {}) };

        if (typeOf === categoryTypeQuestion) {
            nextAnswers.categories[curQuesNo] = response;
        }

        if (typeOf === questionTypeQuestion) {
            const qIdx = (curStep?.quesListNo ?? 1) - 1;
            const qObj = Array.isArray(questionList) ? questionList[qIdx] : undefined;

            const question_text =
                (qObj && (qObj.ques || qObj.question || qObj.text || qObj.label)) ??
                (curStep?.question?.text || curStep?.question || curStep?.label || '');

            const ensuredResponse = {
                ...curStep,               // preserves survey_q_id / ids / order you already track
                ...(response || {}),      // any fields your Question component sends back
                response:
                    response && response.response != null ? response.response : 5, // your existing default
                question_text,            // <-- ✨ new field with the rendered question’s text
            };

            nextAnswers.questions[curQuesNo] = ensuredResponse;
        }

        if (typeOf === feedbackTypeQuestion || typeOf === followupAiTypeQuestion) {
            nextFeedback = nextFeedback.filter(
                fb => String(fb?.survey_q_id) !== String(response?.survey_q_id)
            );
            nextFeedback.push(response);
        }

        if (typeOf === modularOutcomeTypeQuestion) {
            const modularQuestion = get_brief_response?.organization?.styles?.survey_sequence
                ?.find(sc => sc.value === curStep?.value);

            const key = "outcome_question";

            const existing = nextModular[key] || {
                id: modularQuestion?.id,
                label: modularQuestion?.name,
                value: modularQuestion?.value,
                on_date: new Date().toISOString(),
                ask_again: 2,
                completed: true,
                responses: [],
                briefResponseId: get_brief_response?.id,
                brief_schedule_id: get_brief_response?.debrief_schedule,
            };

            const updatedResponses = (existing.responses || []).filter(r => r?.ques_order !== curStep?.id);
            updatedResponses.push(response);

            nextModular[key] = { ...existing, responses: updatedResponses };
        }

        setQuestionAnswers(nextAnswers);
        setSurveyFeedbackResp(nextFeedback);
        setSurveyModularResp(nextModular);

        setQuestionSequenceMap(prev => {
            const next = { ...prev };
            next[curQuesNo] = {
                ...prev[curQuesNo],
                clicked: true,
                response: response || { response: 5 },
            };
            return next;
        });

        setFormPayload(prev => ({
            ...(prev || {}),
            answers: nextAnswers,
            feedback: nextFeedback,
            modular_response: nextModular,
            validation_token: LOCAL_VALIDATION_TOKEN,
        }));
    }

    const [showLoader, setShowLoader] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    useEffect(() => {
        let t;
        // Don't show loader if there's a token error
        if (tokenError) {
            setShowLoader(false);
        } else if (get_brief_response?.pending) {
            // only show after 1000ms to avoid flicker for very fast responses
            t = setTimeout(() => setShowLoader(true),  1000);
        } else {
            setShowLoader(false);
        }
        return () => clearTimeout(t);
    }, [get_brief_response?.pending, tokenError]);


    useEffect(() => {
        if (get_errors?.errors?.error?.request?.response) {
            let RESP = JSON.parse(get_errors?.errors?.error?.request?.response);
            setSubmitError(RESP?.detail);
        }
    }, [get_errors]);

    useEffect(() => {
        // Check for errors from the initial survey fetch
        // Based on actual API response: get_errors.errors.error contains status and detail

        const errorObj = get_errors?.errors?.error;
        const status = errorObj?.status;

        // Common HTTP error codes for bad tokens (400, 401, 403, 404)
        if (status && (status === 400 || status === 401 || status === 403 || status === 404)) {
            // Extract error message from the API response
            const errorMessage = errorObj?.detail ||
                errorObj?.statusText ||
                "Invalid or expired token. Please check your survey link.";

            setTokenError(errorMessage);
        }
    }, [get_errors]);

    const renderQuestion = (currentQuestionDet) => {
        switch (currentQuestionDet?.quesType) {
            case introductionTypeQuestion:
                return (
                    <SurveyIntro
                        id={currentQuestionDet.intro_id}
                        currentQuestion={currentQuestionNo}
                        total={currentQuestionDet.total}
                        language={language}
                        title={
                            organizationStyles?.intro_title
                                ? get_brief_response?.organization_name
                                : false
                        }
                    />
                );

            case categoryTypeQuestion:
                return (
                    <CategoryQuestion
                        question={currentQuestionDet.question}
                        categories={currentQuestionDet.categories}
                        description={currentQuestionDet.description}
                        categoryQues={categoryQues}
                        currentQuestionDet={currentQuestionDet}
                        saveUpdatedAnswers={saveUpdatedAnswers}
                        language={language}
                    />
                );

            case questionTypeQuestion: {
                const idx = (currentQuestionDet?.quesListNo ?? 1) - 1;
                const qObj = Array.isArray(questionList) ? questionList[idx] : undefined;
                return (
                    <SurveyQuestion
                        question={qObj}
                        saveUpdatedAnswers={saveUpdatedAnswers}
                        currentQuestionDet={currentQuestionDet}
                        language={language}
                    />
                );
            }

            case feedbackTypeQuestion: {
                const feedbackForStep = Array.isArray(ruleFeedback)
                    ? ruleFeedback[currentQuestionDet?.feedListNo - 1]
                    : undefined;

                return (
                    <FeedbackQuestion
                        ruleFeedback={feedbackForStep}
                        saveUpdatedAnswers={saveUpdatedAnswers}
                        currentQuestionDet={currentQuestionDet}
                    />
                );
            }
            case followupAiTypeQuestion: {
                const refFollowUpQues = questionSequenceMap?.[currentQuestionDet?.quesRefId];
                if (!refFollowUpQues) return null; // render nothing until ready
                return (
                    <FollowUp
                        currentQuestionDet={currentQuestionDet}
                        refQues={refFollowUpQues}
                        saveUpdatedAnswers={saveUpdatedAnswers}
                        handleNext={handleNext}
                    />
                );
            }
            case modularOutcomeTypeQuestion:
                return (
                    <OutcomeQuestion
                        resetResponsesOnChange={totalQuestions?.resetResponsesOnChange}
                        questionSequenceMap={questionSequenceMap}
                        question={currentQuestionDet?.question}
                        saveUpdatedAnswers={saveUpdatedAnswers}
                        currentQuestionDet={currentQuestionDet}
                        language={language}
                    />
                );
            case completedTypeQuestion:
                return renderStatusScreen(THANKYOU_MESSAGE);
            case submitTypeQuestion:
                return <SurveyCompleted />;
            default:
                return null;
        }
    };

    const renderStatusScreen = (statusText, variant = "success") => {
        const transStatusText = translate(statusText, language);
        const transTitle = translate(DEFAULT_STATUS_TITLE, language);
        const completedOn = surveyDeletedOn;

        return (
            <TransitionGroup>
                <CSSTransition
                    key={currentQuestionNo}
                    classNames={direction}
                    timeout={{ enter: TRANSITION_MS, exit: TRANSITION_MS }}
                    onEntered={() => setIsVisible(true)}
                    onExited={() => setIsVisible(false)}
                >
                    <ModuleContainer className="module-wrapper">
                        <SurveyStatus
                            statusText={transStatusText}
                            title={transTitle}
                            {...(completedOn ? { completedOn } : {})}
                            variant={variant}
                        />
                    </ModuleContainer>
                </CSSTransition>
            </TransitionGroup>
        );
    };

    const renderInfoScreen = (statusText) => {
        const transStatusText = translate(statusText, language);

        return (
            <TransitionGroup>
                <CSSTransition
                    key={currentQuestionNo}
                    classNames={direction}
                    timeout={{ enter: TRANSITION_MS, exit: TRANSITION_MS }}
                    onEntered={() => setIsVisible(true)}
                    onExited={() => setIsVisible(false)}
                >
                    <ModuleContainer className="module-wrapper">
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            padding: '2rem',
                            maxWidth: '600px',
                            margin: '0 auto'
                        }}>
                            {/* Icon */}
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: '#fee',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '1.5rem'
                            }}>
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#dc3545"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                            </div>

                            {/* Title */}
                            <h2 style={{
                                fontSize: '1.75rem',
                                fontWeight: '600',
                                color: '#2a3039',
                                marginBottom: '1rem',
                                fontFamily: '"Raleway", sans-serif'
                            }}>
                                {translate({
                                    EN: "Survey Link Invalid",
                                    FR: "Lien d'enquête invalide"
                                }, language)}
                            </h2>

                            {/* Message */}
                            <p style={{
                                fontSize: '1.1rem',
                                color: '#6c757d',
                                lineHeight: '1.6',
                                marginBottom: '1.5rem',
                                fontFamily: '"Raleway", sans-serif'
                            }}>
                                {transStatusText}
                            </p>

                            {/* Additional Help Text */}
                            <div style={{
                                backgroundColor: '#f8f9fa',
                                padding: '1.25rem',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef',
                                marginTop: '1rem'
                            }}>
                                <p style={{
                                    fontSize: '0.95rem',
                                    color: '#495057',
                                    margin: 0,
                                    lineHeight: '1.5',
                                    fontFamily: '"Raleway", sans-serif'
                                }}>
                                    {translate({
                                        EN: "Please check your survey link or contact the person who sent you this survey for a new link.",
                                        FR: "Veuillez vérifier votre lien d'enquête ou contacter la personne qui vous a envoyé cette enquête pour obtenir un nouveau lien."
                                    }, language)}
                                </p>
                            </div>
                        </div>
                    </ModuleContainer>
                </CSSTransition>
            </TransitionGroup>
        );
    };



    const SubmitErrorDialog = ({ open, onClose, title, body }) => (
        <Modal open={open} onClose={onClose} size="small" closeIcon>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Content>
                <p>{body}</p>
            </Modal.Content>
            <Modal.Actions>
                <Button primary onClick={onClose}>OK</Button>
            </Modal.Actions>
        </Modal>
    );

    const submitErrOpen = Boolean(submitError);
    const submitErrTitle = translate({ EN: "Survey submission challenges", FR: "Erreur de soumission de l'enquête" }, language);
    const submitErrBody =
        typeof submitError === "string"
            ? submitError
            : translate(ERROR_ON_SUBMISSION, language);

    // If there's a token error, display it
    if (tokenError) {
        return renderStatusScreen(MISSING_OR_INVALID_TOKEN, "error");
    }

    // If we're in the initial fetch phase and the token isn't retrieved yet show the full SurveyLoader.
    if (!get_brief_response?.survey_token) {
        return (
            <TransitionGroup>
                <CSSTransition
                    key={currentQuestionNo}
                    classNames={direction}
                    timeout={{ enter: TRANSITION_MS, exit: TRANSITION_MS }}
                    onEntered={() => setIsVisible(true)}
                    onExited={() => setIsVisible(false)}
                >
                    <ModuleContainer className="module-wrapper">
                        <SurveyLoader
                            title="Loading your survey"
                            subtitle="Fetching questions and saved progress…"
                        />
                    </ModuleContainer>
                </CSSTransition>
            </TransitionGroup>
        );
    }

    if (surveyFinished) {
        return renderStatusScreen(THANKYOU_MESSAGE, "success");
    }

    // Check survey status from brief response
    const surveyStatusCode = get_brief_response?.token_status;

    const validStatuses = [0, 1, 2, 3, 5, 6, 7, 9, 100];
    if (surveyStatusCode !== undefined && surveyStatusCode !== null && !validStatuses.includes(surveyStatusCode) && !initialLoad) {
        return renderStatusScreen(SURVEY_STATUS_UNKNOWN, "error");
    }

    // Status 3: Completed
    if (surveyStatusCode === 3 && !initialLoad) {
        return renderStatusScreen(SURVEY_STATUS_COMPLETED, "info");
    }

    // Status 5: Cancelled
    if (surveyStatusCode === 5 && !initialLoad) {
        return renderStatusScreen(SURVEY_STATUS_CANCELLED, "info");
    }

    // Status 7 or 9: No Response or Incomplete - survey is closed
    if ((surveyStatusCode === 7 || surveyStatusCode === 9) && !initialLoad) {
        return renderStatusScreen(SURVEY_STATUS_CLOSED, "info");
    }

    // Status 100: Deleted
    if (surveyStatusCode === 100 && !initialLoad) {
        return renderStatusScreen(SURVEY_STATUS_DELETED, "warning");
    }

    if (!initialLoad && get_brief_response && (get_brief_response.valid_upto < new Date().toISOString() && !get_brief_response?.is_demo)) {
        return renderStatusScreen(SURVEY_STATUS_CLOSED, "info");
    }

    if (!initialLoad && get_brief_response.survey_token && (!get_brief_response.is_schedule_open && !get_brief_response.is_demo)) {
        return renderStatusScreen(ASSESSMENT_PERIOD_NOT_OPEN, "info");
    }

    // ask for existing data in storage
    if (storedResponseFound && !showingStoredResponse) {
        return (
            <SurveyInProgressDialog
                setQuestionNo={setQuestionNo}
                setVisualQuestionNo={setVisualQuestionNo}
                token={token}
                briefResponseId={briefResponseId}
                resetValidationToken={resetValidationToken}
                storedResponse={storedResponse}
                setCurrentQuestionNo={setCurrentQuestionNo}
                setCurrentQuestionDet={setCurrentQuestionDet}
                setQuestionSequenceMap={setQuestionSequenceMap}
                setShowingStoredResponse={setShowingStoredResponse}
                setStoredResponseFound={setStoredResponseFound}
                setStoredResponse={setStoredResponse}
            />
        );
    }

    return (
        <SurveyContainer>
            <SubmitErrorDialog
                open={submitErrOpen}
                onClose={() => setSubmitError(false)}
                title={submitErrTitle}
                body={submitErrBody}
            />

            {/* progress bar when it not introduction */}
            {currentQuestionNo > 4 && !surveyFinished && (
                <ResponseProgressBar
                    currentQuestion={currentQuestionDet?.visualTotal}
                    totalQuestions={totalQuestions?.visualTotal}
                    quesId={currentQuestionDet?.id}
                    is_demo={get_brief_response?.is_demo || false}
                />
            )}

            {/* <Language language={language} setLanguage={setLanguage} /> */}

            <TransitionGroup>
                <CSSTransition
                    key={currentQuestionNo}
                    classNames={direction}
                    timeout={{ enter: TRANSITION_MS, exit: TRANSITION_MS }}
                    onEntered={() => setIsVisible(true)}
                    onExited={() => setIsVisible(false)}
                >
                    <ModuleContainer
                        className="module-wrapper"
                        selected={currentQuestionDet?.quesType}
                    >
                        {renderQuestion(currentQuestionDet)}

                        {/* Compact overlay loader for non-initial loads (Next / Accept clicks) */}
                        {showLoader && !initialLoad && (
                            <div style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(255,255,255,0.6)',
                                zIndex: 9999,
                            }}>
                                <div style={{ textAlign: 'center' }}>
                                    <Loader active inline="centered" />
                                    <div style={{ marginTop: 8, color: '#333' }}>Loading…</div>
                                </div>
                            </div>
                        )}
                    </ModuleContainer>
                </CSSTransition>
            </TransitionGroup>

            <ButtonContainer>
                <SurveyNavigationButtons
                    introQuestion={totalQuestions?.introQuestion}
                    currentQuestionDet={currentQuestionDet}
                    handleBack={handleBack}
                    handleNext={handleNext}
                    surveyFinished={surveyFinished}
                    submitAnswer={submitCompletedSurvey}
                    StartSurvey={StartSurvey}
                    is_demo={get_brief_response?.is_demo || false}
                />
            </ButtonContainer>
        </SurveyContainer>
    );
};

export default SurveyMain;

